import {
    createId,
    nowIso,
    getState,
    mutate,
    delay,
    findUnit,
    UNIT_TYPES,
} from './store';
import { listMatchingAssignments } from '@/utils/corporate/accessRules';
import { normalizeAssignment } from './courseAssignments';

function courseName(courseId, snapshot = getState()) {
    const course = (snapshot.courses || []).find((c) => String(c.id) === String(courseId));
    if (course?.name) return course.name;
    const fallback = {
        course_mock_1: 'Introdução à Empresa',
        course_mock_2: 'Segurança da Informação',
        course_mock_3: 'LGPD na Prática',
        course_mock_intro: 'Regras e Conduta',
        course_mock_rules: 'Melhorias Contínuas',
    };
    return fallback[courseId] || `Curso ${courseId}`;
}

function normalizeItems(items = []) {
    return (items || [])
        .map((item, index) => ({
            courseId: String(item.courseId),
            order: Number(item.order) || index + 1,
            unlockRule: item.unlockRule || 'always',
        }))
        .sort((a, b) => a.order - b.order);
}

function enrichGroup(group, snapshot = getState()) {
    if (!group) return null;
    const items = normalizeItems(group.items).map((item) => ({
        ...item,
        courseName: courseName(item.courseId, snapshot),
        courseStatus: (snapshot.courses || []).find((c) => String(c.id) === String(item.courseId))?.status || 'active',
    }));
    const sectorNames = (group.target?.sectorIds || [])
        .map((id) => findUnit(id, snapshot)?.name)
        .filter(Boolean);
    const roleNames = (group.target?.roleIds || [])
        .map((id) => findUnit(id, snapshot)?.name)
        .filter(Boolean);

    return {
        ...group,
        items,
        itemsCount: items.length,
        targetLabels: {
            sectors: sectorNames,
            roles: roleNames,
        },
    };
}

function matchesTarget(group, { sectorId, roleId, boardId } = {}, snapshot = getState()) {
    const assignments = (snapshot.courseAssignments || [])
        .map((a) => normalizeAssignment(a, snapshot))
        .filter(Boolean);
    const rules = listMatchingAssignments(
        assignments,
        { sectorId, roleId, boardId },
        { resourceType: 'group' },
    );
    return rules.some((rule) => String(rule.resourceId) === String(group.id));
}

function enrolmentStatus(courseId, enrolments = []) {
    const found = enrolments.find((e) => String(e.courseId) === String(courseId));
    return found || { courseId, status: 'not_started', progress: 0 };
}

/**
 * Resolve unlock state for path/group modes based on enrolments.
 */
export function resolveGroupForViewer(group, enrolments = [], snapshot = getState()) {
    const enriched = enrichGroup(group, snapshot);
    if (!enriched) return null;

    const items = enriched.items.map((item, index, list) => {
        const enrolment = enrolmentStatus(item.courseId, enrolments);
        let locked = false;
        let lockReason = null;

        if (enriched.mode === 'path' && item.unlockRule === 'previous_completed' && index > 0) {
            const previous = list[index - 1];
            const prevEnrolment = enrolmentStatus(previous.courseId, enrolments);
            if (prevEnrolment.status !== 'completed') {
                locked = true;
                lockReason = `Conclua "${previous.courseName}" para liberar`;
            }
        }

        return {
            ...item,
            locked,
            lockReason,
            status: enrolment.status,
            progress: enrolment.progress || 0,
            mandatory: true,
            source: 'group',
        };
    });

    return {
        ...enriched,
        items,
        completedCount: items.filter((i) => i.status === 'completed').length,
        unlockedCount: items.filter((i) => !i.locked).length,
    };
}

export default {
    async list({ status, search, mode } = {}) {
        await delay();
        const snapshot = getState();
        let groups = (snapshot.courseGroups || []).map((g) => enrichGroup(g, snapshot));

        if (status) groups = groups.filter((g) => g.status === status);
        if (mode) groups = groups.filter((g) => g.mode === mode);
        if (search) {
            const term = search.toLowerCase();
            groups = groups.filter(
                (g) => g.name.toLowerCase().includes(term)
                    || (g.description || '').toLowerCase().includes(term),
            );
        }

        const content = groups.sort((a, b) => a.name.localeCompare(b.name));
        const courses = (snapshot.courses || []).filter((c) => {
            const type = c?.type ?? c?.courseType;
            return type == null || Number(type) === 1;
        });
        return {
            content,
            totalElements: content.length,
            page: 0,
            size: content.length,
            coursesAvailable: courses,
            groupsAvailable: content,
        };
    },

    async getById(id) {
        await delay();
        const snapshot = getState();
        const group = (snapshot.courseGroups || []).find((g) => g.id === id);
        if (!group) {
            const error = new Error('Grupo não encontrado.');
            error.code = 'NOT_FOUND';
            throw error;
        }
        return enrichGroup(group, snapshot);
    },

    async create(payload = {}) {
        await delay();
        let created = null;
        mutate((draft) => {
            const shortDescription = payload.shortDescription || payload.description || '';
            const approvalEnabled = payload.approvalEnabled != null
                ? !!payload.approvalEnabled
                : Number(payload.approved) > 0;
            const minGrade = approvalEnabled
                ? (Number(payload.minGrade ?? payload.approved) || 0)
                : 0;
            const certificateEnabled = payload.certificateEnabled != null
                ? !!payload.certificateEnabled
                : Number(payload.certificate) === 1;
            const availabilityDeadline = Number(
                payload.availabilityDeadline ?? payload.dayExpired,
            ) || 1;
            created = {
                id: createId('group'),
                companyId: draft.companyId,
                name: payload.name || 'Novo grupo',
                commercialName: payload.commercialName || payload.comercialName || payload.name || '',
                url: payload.url || '',
                description: shortDescription,
                shortDescription,
                longDescription: payload.longDescription || '',
                mode: payload.mode === 'path' ? 'path' : 'group',
                status: payload.status || 'active',
                price: Number(payload.price) || 0,
                discount: Number(payload.discount) || 0,
                installment: Number(payload.installment) || 1,
                approvalEnabled,
                minGrade,
                approved: approvalEnabled ? minGrade : -1,
                certificateEnabled,
                certificate: certificateEnabled ? 1 : 0,
                availabilityDeadline,
                dayExpired: availabilityDeadline,
                imageUrl: payload.imageUrl || null,
                target: {
                    sectorIds: payload.target?.sectorIds || [],
                    roleIds: payload.target?.roleIds || [],
                },
                items: normalizeItems(payload.items),
                createdAt: nowIso(),
                updatedAt: nowIso(),
            };
            draft.courseGroups = [...(draft.courseGroups || []), created];
        });
        return enrichGroup(created);
    },

    async update(id, payload = {}) {
        await delay();
        let updated = null;
        mutate((draft) => {
            const index = (draft.courseGroups || []).findIndex((g) => g.id === id);
            if (index < 0) {
                const error = new Error('Grupo não encontrado.');
                error.code = 'NOT_FOUND';
                throw error;
            }
            const current = draft.courseGroups[index];
            updated = {
                ...current,
                ...payload,
                mode: payload.mode ? (payload.mode === 'path' ? 'path' : 'group') : current.mode,
                target: {
                    sectorIds: payload.target?.sectorIds ?? current.target?.sectorIds ?? [],
                    roleIds: payload.target?.roleIds ?? current.target?.roleIds ?? [],
                },
                items: payload.items ? normalizeItems(payload.items) : current.items,
                updatedAt: nowIso(),
            };
            draft.courseGroups[index] = updated;
        });
        return enrichGroup(updated);
    },

    async remove(id) {
        await delay();
        mutate((draft) => {
            draft.courseGroups = (draft.courseGroups || []).filter((g) => g.id !== id);
        });
        return { ok: true };
    },

    /**
     * Portal: grupos aplicáveis a setor/cargo, com unlock resolvido.
     */
    async listForViewer({ sectorId, roleId, boardId, enrolments } = {}) {
        await delay();
        const snapshot = getState();
        const viewer = { sectorId, roleId, boardId };
        const groups = (snapshot.courseGroups || [])
            .filter((g) => g.status === 'active' || g.status === 'internal' || String(g.status) === '2')
            .filter((g) => matchesTarget(g, viewer, snapshot))
            .map((g) => resolveGroupForViewer(g, enrolments || [], snapshot))
            .filter(Boolean);

        const assignments = (snapshot.courseAssignments || [])
            .map((a) => normalizeAssignment(a, snapshot))
            .filter(Boolean);
        const liberatedCourses = listMatchingAssignments(
            assignments,
            viewer,
            { resourceType: 'course' },
        ).map((rule) => {
            const course = (snapshot.courses || []).find(
                (c) => String(c.id) === String(rule.resourceId),
            );
            return {
                courseId: rule.resourceId,
                courseName: course?.name || rule.resourceId,
                mandatory: Boolean(rule.mandatory),
                deadlineDays: rule.deadlineDays,
            };
        });

        return {
            content: groups,
            totalElements: groups.length,
            liberatedCourses,
        };
    },

    matchesTarget,
    resolveGroupForViewer,
    UNIT_TYPES,
};
